// Despachos interior (admin · Logística) — gestión de la tabla public.despachos.
//
// Los pedidos OneShot de INTERIOR crean un despacho al pagar (trigger tg_pedidos_crear_entrega_fn
// → crear_despacho_desde_pedido). Esta página es donde se opera: asignar courier (COT/DAC),
// cargar tracking y mover el estado (pendiente → despachado → entregado / cancelado).
//
// RLS: is_admin() full (admin + superadmin operan; es tabla operativa, no config).
// Fuente de verdad: public.despachos. No inventa datos.

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import AdminLayout from '../../components/AdminLayout'

const fmt = (n) => '$' + Math.round(Number(n) || 0).toLocaleString('es-UY')
const COURIERS = ['COT', 'DAC']
const ESTADOS = {
  pendiente: { label: 'Pendiente', bg: '#FEF3C7', fg: '#92400E' },
  despachado: { label: 'Despachado', bg: '#DBEAFE', fg: '#1E40AF' },
  entregado: { label: 'Entregado', bg: '#DCFCE7', fg: '#166534' },
  cancelado: { label: 'Cancelado', bg: '#FEE2E2', fg: '#991B1B' },
}
const FILTROS = ['pendiente', 'despachado', 'entregado', 'cancelado', 'todos']

function fechaCorta(s) {
  if (!s) return '—'
  const d = new Date(s)
  return d.toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit' })
}

export default function Despachos() {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [filtro, setFiltro] = useState('pendiente')
  const [savingId, setSavingId] = useState(null)
  const [trackDraft, setTrackDraft] = useState({}) // id -> texto en edición
  const [msg, setMsg] = useState(null)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true); setMsg(null)
    try {
      const { data, error } = await supabase
        .from('despachos')
        .select('id, numero_orden, comprador_nombre, comprador_telefono, comprador_documento, localidad, direccion, envio_zona_codigo, courier, monto_total, costo_envio, estado, tracking, fecha_programada, despachado_at, entregado_at, notas, created_at')
        .eq('es_prueba', false)
        .order('created_at', { ascending: false })
      if (error) throw error
      setRows(data || [])
    } catch (e) {
      setMsg({ ok: false, txt: 'Error cargando despachos: ' + (e.message || e) })
    } finally {
      setLoading(false)
    }
  }

  async function patch(id, campos, okTxt) {
    setSavingId(id); setMsg(null)
    try {
      const { error } = await supabase.from('despachos').update({ ...campos, updated_at: new Date().toISOString() }).eq('id', id)
      if (error) throw error
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...campos } : r)))
      if (okTxt) setMsg({ ok: true, txt: okTxt })
    } catch (e) {
      setMsg({ ok: false, txt: 'Error al guardar: ' + (e.message || e) })
    } finally {
      setSavingId(null)
    }
  }

  const setCourier = (id, courier) => patch(id, { courier: courier || null }, '✓ Courier asignado.')
  const guardarTracking = (id) => patch(id, { tracking: (trackDraft[id] ?? '').trim() || null }, '✓ Tracking guardado.')
  const marcar = (id, estado) => {
    const campos = { estado }
    if (estado === 'despachado') campos.despachado_at = new Date().toISOString()
    if (estado === 'entregado') campos.entregado_at = new Date().toISOString()
    patch(id, campos, '✓ Estado actualizado a ' + ESTADOS[estado].label + '.')
  }

  const kpis = useMemo(() => {
    const k = { total: rows.length, pendiente: 0, despachado: 0, entregado: 0, cancelado: 0 }
    rows.forEach((r) => { k[r.estado] = (k[r.estado] || 0) + 1 })
    return k
  }, [rows])

  const visibles = useMemo(() => (filtro === 'todos' ? rows : rows.filter((r) => r.estado === filtro)), [rows, filtro])

  return (
    <AdminLayout>
      <div className="page desp">
        <Link to="/admin/logistica/limpackflex" style={{ fontSize: 13, color: 'var(--muted)' }}>← LimpackFlex</Link>
        <h1 style={{ margin: '8px 0 0' }}>🚚 Despachos al interior</h1>
        <p className="lead" style={{ margin: '4px 0 0', maxWidth: 720 }}>
          Pedidos OneShot del interior. Se crean solos al pagar (van a Terminal Tres Cruces). Asigná courier, cargá el tracking y movelos de estado. La encomienda la paga el cliente al retirar.
        </p>

        {msg && (
          <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, background: msg.ok ? '#DCFCE7' : '#FEE2E2', color: msg.ok ? '#166534' : '#7F1D1D', fontSize: 13, fontWeight: 600 }}>{msg.txt}</div>
        )}

        <div className="desp-kpis">
          {['pendiente', 'despachado', 'entregado', 'total'].map((k) => (
            <div className="desp-kpi" key={k} style={{ borderTopColor: k === 'total' ? '#1A3461' : ESTADOS[k]?.fg }}>
              <div className="desp-kpi-n">{kpis[k] ?? 0}</div>
              <div className="desp-kpi-l">{k === 'total' ? 'Total' : ESTADOS[k].label}</div>
            </div>
          ))}
        </div>

        <div className="desp-tabs">
          {FILTROS.map((f) => (
            <button key={f} className={'desp-tab' + (filtro === f ? ' on' : '')} onClick={() => setFiltro(f)}>
              {f === 'todos' ? 'Todos' : ESTADOS[f].label}
              <span className="desp-tab-n">{f === 'todos' ? rows.length : (kpis[f] ?? 0)}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="card" style={{ marginTop: 16, color: 'var(--muted)' }}>Cargando…</div>
        ) : visibles.length === 0 ? (
          <div className="desp-empty">
            <div style={{ fontSize: 40 }}>📦</div>
            <h3>No hay despachos {filtro !== 'todos' ? ESTADOS[filtro].label.toLowerCase() : ''}</h3>
            <p>Cuando se pague un pedido del interior, aparece acá automáticamente.</p>
          </div>
        ) : (
          <div className="desp-tablewrap">
            <table className="desp-table">
              <thead><tr>
                <th>Orden</th><th>Comprador</th><th>Destino</th><th>Monto</th><th>Courier</th><th>Tracking</th><th>Estado</th><th>Acciones</th>
              </tr></thead>
              <tbody>
                {visibles.map((r) => {
                  const busy = savingId === r.id
                  const est = ESTADOS[r.estado] || ESTADOS.pendiente
                  return (
                    <tr key={r.id} style={{ opacity: busy ? 0.6 : 1 }}>
                      <td>
                        <div className="desp-orden">{r.numero_orden}</div>
                        <div className="desp-sub">{fechaCorta(r.created_at)}</div>
                      </td>
                      <td>
                        <div className="desp-nom">{r.comprador_nombre || '—'}</div>
                        <div className="desp-sub">{r.comprador_telefono || ''}{r.comprador_documento ? ' · CI ' + r.comprador_documento : ''}</div>
                      </td>
                      <td>
                        <div>{r.localidad || '—'}</div>
                        <div className="desp-sub">{r.direccion || ''}</div>
                      </td>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(r.monto_total)}
                        <div className="desp-sub">envío {fmt(r.costo_envio)}</div>
                      </td>
                      <td>
                        <select className="desp-inp" value={r.courier || ''} disabled={busy} onChange={(e) => setCourier(r.id, e.target.value)}>
                          <option value="">— sin asignar</option>
                          {COURIERS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td>
                        <div className="desp-track">
                          <input className="desp-inp" placeholder="nº seguimiento"
                            value={trackDraft[r.id] ?? r.tracking ?? ''} disabled={busy}
                            onChange={(e) => setTrackDraft((d) => ({ ...d, [r.id]: e.target.value }))} />
                          <button className="desp-mini" disabled={busy} onClick={() => guardarTracking(r.id)} title="Guardar tracking">✓</button>
                        </div>
                      </td>
                      <td><span className="desp-badge" style={{ background: est.bg, color: est.fg }}>{est.label}</span></td>
                      <td>
                        <div className="desp-acts">
                          {r.estado === 'pendiente' && <button className="desp-act blue" disabled={busy} onClick={() => marcar(r.id, 'despachado')}>Despachar</button>}
                          {r.estado === 'despachado' && <button className="desp-act green" disabled={busy} onClick={() => marcar(r.id, 'entregado')}>Entregar</button>}
                          {(r.estado === 'pendiente' || r.estado === 'despachado') && <button className="desp-act ghost" disabled={busy} onClick={() => marcar(r.id, 'cancelado')}>Cancelar</button>}
                          {r.estado === 'entregado' && <span className="desp-sub">✓ {fechaCorta(r.entregado_at)}</span>}
                          {r.estado === 'cancelado' && <span className="desp-sub">—</span>}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .desp code { background:#f1f5f9; padding:1px 5px; border-radius:4px; }
        .desp-kpis { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-top:18px; }
        .desp-kpi { background:#fff; border:1px solid #EEF2F7; border-top:3px solid #1A3461; border-radius:16px; padding:14px; text-align:center; box-shadow:0 2px 4px rgba(15,31,61,.04), 0 6px 16px rgba(15,31,61,.07); }
        .desp-kpi-n { font-size:26px; font-weight:800; color:#1A3461; font-family:'Manrope','Inter',system-ui,sans-serif; letter-spacing:-.02em; font-variant-numeric:tabular-nums; }
        .desp-kpi-l { font-size:11px; color:#64748b; font-weight:600; text-transform:uppercase; letter-spacing:.04em; }
        .desp-tabs { display:flex; gap:6px; margin:18px 0 0; flex-wrap:wrap; }
        .desp-tab { background:#fff; border:1px solid #E2E8F0; border-radius:9px; padding:8px 12px; font-size:12.5px; font-weight:700; color:#64748b; cursor:pointer; display:flex; align-items:center; gap:7px; font-family:inherit; }
        .desp-tab.on { background:#1A3461; color:#fff; border-color:#1A3461; box-shadow:0 2px 4px rgba(15,31,61,.04), 0 6px 16px rgba(15,31,61,.07); }
        .desp-tab-n { font-size:11px; background:rgba(148,163,184,.25); color:inherit; padding:1px 7px; border-radius:999px; }
        .desp-tab.on .desp-tab-n { background:rgba(255,255,255,.22); }
        .desp-tablewrap { margin-top:14px; background:#fff; border:1px solid #EEF2F7; border-radius:16px; overflow:auto; box-shadow:0 2px 4px rgba(15,31,61,.04), 0 6px 16px rgba(15,31,61,.07); }
        .desp-table { width:100%; border-collapse:collapse; font-size:13px; }
        .desp-table th { text-align:left; padding:11px 12px; background:#f8fafc; border-bottom:1px solid #e5e7eb; font-size:10.5px; text-transform:uppercase; letter-spacing:.04em; color:#94a3b8; font-weight:800; white-space:nowrap; }
        .desp-table td { padding:11px 12px; border-bottom:1px solid #f1f5f9; vertical-align:top; }
        .desp-orden { font-weight:800; color:#1A3461; }
        .desp-nom { font-weight:700; color:#0f172a; }
        .desp-sub { font-size:11px; color:#94a3b8; margin-top:2px; }
        .desp-inp { width:100%; min-width:110px; padding:6px 8px; border:1px solid #CBD5E1; border-radius:7px; font-size:12.5px; font-family:inherit; color:#1A3461; }
        .desp-track { display:flex; gap:5px; align-items:center; }
        .desp-mini { flex-shrink:0; width:30px; height:30px; border:1px solid #00A8B5; background:#ecfeff; color:#0e7490; border-radius:7px; font-weight:800; cursor:pointer; }
        .desp-badge { font-size:11px; font-weight:800; padding:3px 10px; border-radius:999px; white-space:nowrap; }
        .desp-acts { display:flex; gap:6px; flex-wrap:wrap; align-items:center; }
        .desp-act { border:none; border-radius:7px; padding:6px 11px; font-size:12px; font-weight:800; cursor:pointer; font-family:inherit; }
        .desp-act.blue { background:#1E40AF; color:#fff; }
        .desp-act.green { background:#059669; color:#fff; }
        .desp-act.ghost { background:#f1f5f9; color:#64748b; }
        .desp-act:disabled, .desp-mini:disabled { opacity:.5; cursor:not-allowed; }
        .desp-empty { margin-top:16px; background:#fff; border:1px solid #EEF2F7; border-radius:16px; padding:48px 20px; text-align:center; color:#64748b; box-shadow:0 2px 4px rgba(15,31,61,.04), 0 6px 16px rgba(15,31,61,.07); }
        .desp-empty h3 { color:#1A3461; margin:12px 0 6px; font-size:15px; }
        .desp-empty p { font-size:13px; margin:0; }
      `}</style>
    </AdminLayout>
  )
}
